import { generateClient } from "aws-amplify/data"
import { useQuery } from "react-query"
import type Stripe from "stripe"
import { type Schema, StripeProduct } from '@backend/data/resource';
import { Play, PlayFolder, UserLicense } from "./types";

const client = generateClient<Schema>();

export const useUserLicensesQuery = () => {
  return useQuery<UserLicense[]>({
    queryKey: ["licenses"],
    queryFn: async (): Promise<UserLicense[]> => {
      let allLicenses: UserLicense[] = [];
      let nextToken: string | undefined | null = undefined;

      do {
        const { data: licenses, errors, nextToken: nT } = await client.models.UserLicense.list({ nextToken, limit: 150 })
        if (!licenses && errors && errors.length > 0) {
          throw new Error(errors.map(e => e.message).join(", "))
        }
        if (licenses && licenses.length > 0) {
          allLicenses = allLicenses.concat(licenses as UserLicense[])
        }
        nextToken = nT
      } while (nextToken)

      return allLicenses
    },
  })
}

export const usePlaysQuery = ({
  enabled = true,
  refetchOnWindowFocus = true
}: {
  userId?: string
  enabled: boolean
  refetchOnWindowFocus?: boolean
}) => {
  return useQuery<Play[]>({
    queryKey: ["plays"],
    queryFn: async (): Promise<Play[]> => {
    let allPlays: Play[] = [];
    let nextToken: string | undefined | null = undefined;

    do {
      const { data: plays, errors, nextToken: nT } = await client.models.Play.list({ nextToken, limit: 150 })
      if (!plays && errors && errors.length > 0) {
        throw new Error(errors.map(e => e.message).join(", "))
      }
      if (plays && plays.length > 0) {
        allPlays = allPlays.concat(plays as Play[])
      }
      nextToken = nT

    } while (nextToken)

     console.log("allPlays", allPlays)
     return allPlays
    },
    enabled: enabled,
    refetchOnWindowFocus: refetchOnWindowFocus ? 'always' : false
  })
}

export const usePlayFoldersQuery = ({
  enabled = true,
}: {
  userId?: string
  enabled: boolean
}) => {
  return useQuery({
    queryKey: ["playFolders"],
    queryFn: async (): Promise<PlayFolder[]> => {

      let allFolders: PlayFolder[] = [];
      let nextToken: string | undefined | null = undefined;
      do {
        const { data: folders, errors, nextToken: nT } = await client.models.PlayFolder.list({ nextToken, limit: 150 })
        if (!folders && errors && errors.length > 0) {
          throw new Error(errors.map(e => e.message).join(", "))
        }
        if (folders && folders.length > 0) {
          allFolders = allFolders.concat(folders as PlayFolder[])
        }
        nextToken = nT

      } while (nextToken)

      return allFolders

    },
    enabled: enabled,
  })
}

export const useStripeSessionQuery = ({
  enabled = true,
  sessionId
}: {
  enabled?: boolean
  sessionId: string
}) => {
  return useQuery({
    queryKey: ["stripe-session", sessionId],
    queryFn: async (): Promise<Stripe.Checkout.Session> => {
     const { data: session, errors } = await client.queries.stripeSessionState({ sessionId })
      if (!session && errors && errors.length > 0) {
        throw new Error(errors.map(e => e.message).join(", "))
      }
     return (JSON.parse(session as string)) as Stripe.Checkout.Session
    },
    refetchOnWindowFocus: false,
    enabled: enabled,
  })
}

export const useGetExtraUserPropertiesQuery = ({ enabled = true }: { enabled?: boolean } = {}) => {
  return useQuery({
    queryKey: ["extra-user-props"],
    queryFn: async (): Promise<{ isEarlyBird: boolean }> => {
      const { data: userProps, errors } = await client.queries.getExtraUserProperties()
      if (!userProps && errors && errors.length > 0) {
        throw new Error(errors.map(e => e.message).join(", "))
      }
      return userProps as { isEarlyBird: boolean }
    },
    enabled: enabled,
  })
}

export const useStripeListPackagesQuery = ({ enabled = true }: { enabled?: boolean } = {}) => {
    return useQuery({
        queryKey: ["stripe-packages"],
        queryFn: async (): Promise<Array<StripeProduct>> => {
        const { data: packages, errors } = await client.queries.stripeListPackages()
        if (!packages && errors && errors.length > 0) {
            throw new Error(errors.map(e => e.message).join(", "))
        }
        return packages as unknown as Array<StripeProduct>
        },
        enabled: !!enabled
    })
}

type StripeCustomerGetNextInvoiceResponse = {
  subscriptionId: string;
  status: string;
  currentPeriodEnd?: number;
  trialEnd?: number | null;
  nextInvoice?: {
    id: string;
    amountDue: number;
    nextPaymentAttempt: number | null;
    currency: string;
  };
};
export const useStripeCustomerGetNextInvoiceQuery = ({ enabled = true }: { enabled?: boolean } = {}) => {
    return useQuery({
        queryKey: ["stripe-next-invoice"],
        queryFn: async (): Promise<StripeCustomerGetNextInvoiceResponse> => {
            const { data: invoice, errors } = await client.queries.stripeCustomerGetNextInvoice()
            if (!invoice && errors && errors.length > 0) {
                throw new Error(errors.map(e => e.message).join(", "))
            }
            return invoice as StripeCustomerGetNextInvoiceResponse
        },
        enabled: enabled,
    })
}